import { Mail, Clock, FileText, ShieldCheck, ScanEye, Image as ImageIcon, UserX } from "lucide-react";
import { Reveal } from "./Reveal";
import { BrandLabel } from "./BrandLabel";

const PRIVACY_ITEMS = [
  {
    icon: ScanEye,
    title: "Zero tracking",
    body: "We do not track your location, profile your shopping habits or sell your scan history to data brokers.",
  },
  {
    icon: ImageIcon,
    title: "Image Processing",
    body: "Scan photos are never stored or used to build advertising profiles.",
  },
  {
    icon: UserX,
    title: "No mandatory accounts",
    body: "You can use WhatThe? Scan without registering an email address or creating an account.",
  },
];

export const Support = () => (
  <section id="support" className="relative py-20 md:py-28">
    <div className="mx-auto max-w-[1240px] px-5 md:px-8">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate mb-3">
              Support
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink leading-[1.1]">
              We're here to help
            </h2>
            <p className="mt-4 text-[15px] md:text-base text-slate leading-relaxed max-w-lg">
              Any questions? Reach out directly to our Australian team.
            </p>
          </Reveal>
        </div>

        <div className="lg:col-span-6">
          <Reveal delay={0.1}>
            <div className="rounded-3xl bg-white border border-line p-7 md:p-8 shadow-soft">
              <a
                href="mailto:info@whatthescan.app"
                data-testid="support-email"
                className="group flex items-center gap-4"
              >
                <span className="flex h-12 w-12 flex-none items-center justify-center rounded-2xl bg-food-bg">
                  <Mail className="h-5 w-5 text-food-accent" strokeWidth={1.8} />
                </span>
                <span className="leading-tight">
                  <span className="block text-xs font-semibold uppercase tracking-[0.12em] text-slate">
                    Email us
                  </span>
                  <span className="block text-lg font-bold tracking-tight text-ink transition-colors group-hover:text-food-accent">
                    info@whatthescan.app
                  </span>
                </span>
              </a>
              <div className="mt-6 flex items-center gap-2.5 border-t border-line pt-5 text-[15px] text-slate">
                <Clock className="h-4 w-4 flex-none text-food-accent" strokeWidth={1.8} />
                We aim to reply to all queries within 24–48 business hours.
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export const Legal = () => (
  <section id="legal" className="relative py-20 md:py-28 bg-white border-y border-line">
    <div className="mx-auto max-w-[1240px] px-5 md:px-8">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate mb-3">
          Legal & compliance
        </p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink leading-[1.1]">
          Transparent by default
        </h2>
      </Reveal>

      <div className="mt-12 grid lg:grid-cols-2 gap-6">
        <Reveal delay={0.05}>
          <div id="terms" className="h-full scroll-mt-28 rounded-3xl bg-page border border-line p-7 md:p-8 shadow-soft">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-food-bg">
                <FileText className="h-5 w-5 text-food-accent" strokeWidth={1.8} />
              </span>
              <h3 className="text-xl font-bold tracking-tight text-ink">Terms of Service</h3>
            </div>
            <div className="mt-5 rounded-2xl bg-food-bg/60 border border-food-accent/15 p-4">
              <p className="text-[15px] leading-relaxed text-food-text">
                <span className="font-bold">Disclaimer:</span>{" "}
                <BrandLabel text="WhatThe? Scan provides informational and educational analysis powered by artificial intelligence, which can occasionally produce inaccurate results. This does not constitute medical, dietary or healthcare advice. Always consult a qualified health professional and check physical packaging for allergens before making decisions based on scan results. Nutrition estimates from the plate scanner are based on standard preparation methods and may not account for hidden seasonings or variations in ingredients." />
              </p>
            </div>
            <p className="mt-4 text-[15px] leading-relaxed text-slate">
              <BrandLabel text="Purchasing one or both WhatThe? Scan modules gives you personal, lifetime access. Fair-use scan limits apply. Top-ups available." />
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12}>
          <div id="privacy" className="h-full scroll-mt-28 rounded-3xl bg-page border border-line p-7 md:p-8 shadow-soft">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-food-bg">
                <ShieldCheck className="h-5 w-5 text-food-accent" strokeWidth={1.8} />
              </span>
              <h3 className="text-xl font-bold tracking-tight text-ink">Privacy Policy</h3>
            </div>
            <ul className="mt-5 space-y-5">
              {PRIVACY_ITEMS.map((p) => {
                const Icon = p.icon;
                return (
                  <li key={p.title} className="flex gap-3.5">
                    <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-xl bg-white border border-line">
                      <Icon className="h-4 w-4 text-food-accent" strokeWidth={1.8} />
                    </span>
                    <span className="leading-relaxed">
                      <span className="block text-[15px] font-bold text-ink">{p.title}</span>
                      <span className="block text-[15px] text-slate"><BrandLabel text={p.body} /></span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
